import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePaso } from '../ui/navegacionReact';
import {
  Lightbulb, Wifi, WifiOff, MapPin, Plus, Loader2, Check, Copy, Eye,
  ChevronRight, ChevronLeft, Power, Radio,
} from 'lucide-react';
import { api, ErrorApi } from '../api';
import { useAuth } from '../lib/contextoAuth';
import { useAvisos } from '../lib/contextoAvisos';
import EstadoVacio from '../ui/EstadoVacio';
import NotaExplicativa from '../ui/NotaExplicativa';
import Modal from '../ui/Modal';
import TiempoRelativo from '../ui/TiempoRelativo';
import { olvidarDispositivos } from '../ui/dispositivosCache';

// Las tiras de luz del almacén: verlas, instalarlas y probarlas.
//
// PARA QUÉ SIRVE ESTA PANTALLA. Para que encontrar algo en un almacén deje
// de ser leer un código y caminar contando charolas. Se busca en TEGO y la
// charola se enciende.
//
// LO QUE NO HACE. No es un panel de diagnóstico. Aquí no se habla de
// canales, ni de mensajes, ni de protocolo: se habla de aparatos que están
// enchufados o no, y de qué charola enciende cada uno.

// Cada 15 s. Un aparato late cada 30 y se da por caído a los 90, así que
// esto enseña el cambio de estado con un latido de retraso como mucho.
const REFRESCO_MS = 15000;

const COLORES = [
  { id: 'verde', etiqueta: 'Verde', muestra: '#22c55e' },
  { id: 'amarillo', etiqueta: 'Amarillo', muestra: '#eab308' },
  { id: 'rojo', etiqueta: 'Rojo', muestra: '#ef4444' },
  { id: 'azul', etiqueta: 'Azul', muestra: '#3b82f6' },
  { id: 'blanco', etiqueta: 'Blanco', muestra: '#f8fafc' },
];

export default function Dispositivos() {
  const [lista, setLista] = useState(null);
  const [salud, setSalud] = useState(null);
  const [error, setError] = useState(null);
  const [asistente, setAsistente] = useState(false);
  const [configurando, setConfigurando] = useState(null);
  const { tieneNivel } = useAuth();
  const puedeConfigurar = tieneNivel('ENCARGADO');

  const cargar = useCallback(
    () => Promise.all([api.get('/dispositivos'), api.get('/dispositivos/salud')])
      .then(([d, s]) => { setLista(d); setSalud(s); setError(null); })
      .catch((e) => {
        setError(e instanceof ErrorApi ? e.message
          : 'No se pudo leer la lista de dispositivos. Vuelve a intentarlo.');
        setLista((v) => v ?? []);
      }),
    [],
  );

  useEffect(() => {
    let vivo = true;
    // SE REFRESCA SOLO. Quien está instalando una tira tiene las dos manos
    // ocupadas subido a una escalera: pedirle que le dé a «actualizar»
    // para ver si su aparato apareció es pedirle que baje.
    cargar();
    const t = setInterval(() => { if (vivo) cargar(); }, REFRESCO_MS);
    return () => { vivo = false; clearInterval(t); };
  }, [cargar]);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Dispositivos</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Las tiras de luz del almacén: las que encienden la charola exacta
            cuando alguien busca un producto.
          </p>
        </div>
        {puedeConfigurar && (
          <button
            type="button"
            onClick={() => setAsistente(true)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                       bg-[var(--color-marca)] text-white hover:opacity-90"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Conectar un dispositivo nuevo
          </button>
        )}
      </div>

      {error && (
        <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {salud && salud.total > 0 && <PanelSalud salud={salud} />}

      {lista === null ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-10 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Buscando dispositivos…
        </div>
      ) : (lista || []).length === 0 ? (
        <EstadoVacio
          icono={Lightbulb}
          titulo="Todavía no hay ningún dispositivo"
          descripcion={
            'Una tira de luz se monta en un pasillo y enciende la charola donde '
            + 'está lo que alguien busca. TEGO funciona igual sin ellas: son un '
            + 'atajo para no caminar contando estantes.'
          }
          accion={puedeConfigurar && (
            <button
              type="button"
              onClick={() => setAsistente(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                         bg-[var(--color-marca)] text-white hover:opacity-90"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Conectar el primero
            </button>
          )}
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {(lista || []).map((d) => (
            <Tarjeta
              key={d.id}
              d={d}
              puedeConfigurar={puedeConfigurar}
              onConfigurar={() => setConfigurando(d)}
              onCambio={cargar}
            />
          ))}
        </div>
      )}

      {asistente && (
        <AsistenteInstalacion
          lista={lista || []}
          onCerrar={() => { setAsistente(false); cargar(); }}
          onConfigurar={(d) => { setAsistente(false); setConfigurando(d); }}
        />
      )}
      {configurando && (
        <ModalConfigurar
          d={configurando}
          onCerrar={() => setConfigurando(null)}
          onGuardado={() => { setConfigurando(null); olvidarDispositivos(); cargar(); }}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------------ salud
function PanelSalud({ salud }) {
  // Verde bien, amarillo atención, rojo problema — los mismos colores que
  // en el resto del sistema.
  const tarjetas = [
    { n: salud.en_linea, t: 'Respondiendo', c: 'text-green-700 bg-green-50 border-green-200' },
    ...(salud.caidos > 0
      ? [{ n: salud.caidos, t: salud.caidos === 1 ? 'Sin responder' : 'Sin responder',
        c: 'text-red-700 bg-red-50 border-red-200' }] : []),
    ...(salud.sin_ubicacion > 0
      ? [{ n: salud.sin_ubicacion, t: 'Sin lugar asignado',
        c: 'text-amber-800 bg-amber-50 border-amber-200' }] : []),
  ];
  return (
    <div className="space-y-3">
      <div className="flex gap-3 flex-wrap">
        {tarjetas.map((x) => (
          <div key={x.t} className={`rounded-xl border px-4 py-2.5 ${x.c}`}>
            <div className="text-2xl font-semibold leading-tight">{x.n}</div>
            <div className="text-xs font-semibold">{x.t}</div>
          </div>
        ))}
      </div>
      {salud.caidos > 0 && (
        <NotaExplicativa titulo="Hay tiras que no están respondiendo">
          Revisa el enchufe y la conexión WiFi de cada una. Mientras no
          respondan, el botón de «Iluminar ubicación» no aparecerá para sus
          estantes — <strong>buscar, contar y vender siguen funcionando
          igual</strong>.
        </NotaExplicativa>
      )}
      {salud.sin_ubicacion > 0 && (
        <NotaExplicativa titulo="Hay tiras que no saben dónde están">
          Aparecieron solas al enchufarlas, pero no tienen un pasillo
          asignado y no pueden encender nada. Ábrelas y dile a cada una
          en qué pasillo la montaste.
        </NotaExplicativa>
      )}
    </div>
  );
}

// --------------------------------------------------------------- tarjeta
function Tarjeta({ d, puedeConfigurar, onConfigurar, onCambio }) {
  const [ocupado, setOcupado] = useState(null);
  const { exito, error: avisoError } = useAvisos();
  const mapa = d.configuracion?.mapa || {};
  const estado = d.ultimo_estado || {};

  async function accion(ruta, nombre, mensaje) {
    setOcupado(nombre);
    try {
      const r = await api.post(`/dispositivos/${d.id}/${ruta}`, {});
      if (r.entregado) exito(mensaje);
      else avisoError(r.detalle || 'No se pudo mandar la señal a la tira.');
    } catch (e) {
      avisoError(e instanceof ErrorApi ? e.message : 'No se pudo mandar la señal.');
    } finally {
      setOcupado(null);
      onCambio?.();
    }
  }

  return (
    <div className={`rounded-xl border p-4 space-y-3 ${
      d.en_linea ? 'border-gray-200 bg-white' : 'border-red-200 bg-red-50/40'}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-gray-900 truncate">{d.nombre}</div>
          <div className="text-xs text-gray-500 truncate">
            Etiqueta del aparato: {d.identificador}
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded shrink-0 ${
          d.en_linea ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
        >
          {d.en_linea
            ? <Wifi className="h-3.5 w-3.5" aria-hidden="true" />
            : <WifiOff className="h-3.5 w-3.5" aria-hidden="true" />}
          {d.en_linea ? 'Respondiendo' : 'Sin responder'}
        </span>
      </div>

      <dl className="text-sm space-y-1">
        <div className="flex gap-2">
          <dt className="text-gray-500 shrink-0">Montada en:</dt>
          <dd className="text-gray-900 font-medium truncate">
            {d.ubicacion || <span className="text-amber-800">Todavía sin asignar</span>}
          </dd>
        </div>
        <div className="flex gap-2">
          <dt className="text-gray-500 shrink-0">Se supo de ella:</dt>
          <dd className="text-gray-900"><TiempoRelativo valor={d.ultimo_latido} /></dd>
        </div>
        <div className="flex gap-2">
          <dt className="text-gray-500 shrink-0">Estantes que enciende:</dt>
          <dd className="text-gray-900">
            {Object.keys(mapa).length || <span className="text-amber-800">Ninguno todavía</span>}
          </dd>
        </div>
        {/* La señal solo se enseña cuando es mala: un número que casi
            siempre está bien entrena a no mirarlo. */}
        {typeof estado.wifi_dbm === 'number' && estado.wifi_dbm < -75 && (
          <div className="flex gap-2">
            <dt className="text-gray-500 shrink-0">WiFi:</dt>
            <dd className="text-amber-800 font-medium">
              Señal débil — se va a desconectar a ratos. Acerca el repetidor.
            </dd>
          </div>
        )}
      </dl>

      {!d.en_linea && (
        <p className="text-xs text-red-800 bg-red-100 rounded-lg px-2.5 py-2 leading-relaxed">
          Esta tira no responde desde hace rato. Revisa el enchufe y la
          conexión WiFi; si está encendida, apágala y vuelve a encenderla.
        </p>
      )}

      <div className="flex gap-2 flex-wrap pt-1">
        {puedeConfigurar && (
          <>
            <button
              type="button"
              onClick={() => accion('parpadear', 'parpadear',
                `La tira «${d.nombre}» está parpadeando. Mírala para confirmar cuál es.`)}
              disabled={!d.en_linea || ocupado !== null}
              title={d.en_linea ? 'Hace parpadear la tira para saber cuál es'
                : 'No se puede: esta tira no está respondiendo'}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5
                         rounded-lg border border-gray-300 bg-white text-gray-700
                         hover:bg-gray-50 disabled:opacity-50"
            >
              {ocupado === 'parpadear'
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                : <Eye className="h-3.5 w-3.5" aria-hidden="true" />}
              Hacer parpadear
            </button>
            <button
              type="button"
              onClick={() => accion('apagar', 'apagar', `Se apagó la tira «${d.nombre}».`)}
              disabled={!d.en_linea || ocupado !== null}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5
                         rounded-lg border border-gray-300 bg-white text-gray-700
                         hover:bg-gray-50 disabled:opacity-50"
            >
              <Power className="h-3.5 w-3.5" aria-hidden="true" />
              Apagar
            </button>
            <button
              type="button"
              onClick={onConfigurar}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5
                         rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            >
              <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
              Configurar
            </button>
          </>
        )}
      </div>
      {/* PREVENIR MEJOR QUE REGAÑAR: el botón deshabilitado dice POR QUÉ,
          en vez de dejar a alguien pulsando algo que no responde. */}
      {puedeConfigurar && !d.en_linea && (
        <p className="text-xs text-gray-500">
          Parpadear y apagar están apagados: esta tira no responde.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------- asistente de alta
const PASOS = ['Qué necesitas', 'Enchúfala', 'Aparece sola', 'Dónde está'];

function AsistenteInstalacion({ lista, onCerrar, onConfigurar }) {
  const [paso, setPaso] = useState(0);
  usePaso('instalar-dispositivo', paso, setPaso);  // atrás = el paso anterior; en el primero cierra el asistente
  const [cred, setCred] = useState(null);
  const [errorCred, setErrorCred] = useState(null);
  const [nuevos, setNuevos] = useState([]);
  const { tieneNivel } = useAuth();
  // Los que ya existían al abrir el asistente. Lo que aparezca a partir de
  // ahora es el aparato que esta persona está instalando ahora mismo.
  const previos = useRef(new Set((lista || []).map((d) => d.identificador)));

  useEffect(() => {
    if (!tieneNivel('ADMIN')) return;
    api.get('/dispositivos/credencial')
      .then(setCred)
      .catch((e) => setErrorCred(e instanceof ErrorApi ? e.message
        : 'No se pudieron leer los datos de conexión.'));
  }, [tieneNivel]);

  // Mientras el paso 3 está abierto, se mira si apareció alguien nuevo.
  useEffect(() => {
    if (paso !== 2) return undefined;
    let vivo = true;
    const mirar = () => api.get('/dispositivos').then((d) => {
      if (!vivo) return;
      setNuevos(d.filter((x) => !previos.current.has(x.identificador)));
    }).catch(() => {});
    mirar();
    const t = setInterval(mirar, 4000);
    return () => { vivo = false; clearInterval(t); };
  }, [paso]);

  const puedeSeguir = paso !== 2 || nuevos.length > 0;

  return (
    <Modal
      titulo="Conectar un dispositivo nuevo"
      subtitulo={`Paso ${paso + 1} de ${PASOS.length} · ${PASOS[paso]}`}
      icono={Lightbulb}
      ancho="max-w-xl"
      onCerrar={onCerrar}
      pie={(
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => (paso === 0 ? onCerrar() : setPaso(paso - 1))}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold
                       border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
          >
            {paso > 0 && <ChevronLeft className="h-4 w-4" aria-hidden="true" />}
            {paso === 0 ? 'Cancelar' : 'Atrás'}
          </button>
          {paso < PASOS.length - 1 ? (
            <button
              type="button"
              disabled={!puedeSeguir}
              onClick={() => setPaso(paso + 1)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold
                         bg-[var(--color-marca)] text-white hover:opacity-90 disabled:opacity-50"
            >
              Siguiente
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
          ) : (
            <button
              type="button"
              onClick={onCerrar}
              className="px-3 py-2 rounded-lg text-sm font-semibold
                         bg-[var(--color-marca)] text-white hover:opacity-90"
            >
              Terminar
            </button>
          )}
        </div>
      )}
    >
      {/* Progreso visible: nadie debe preguntarse cuánto falta. */}
      <div className="flex gap-1.5 px-4 pt-4" aria-hidden="true">
        {PASOS.map((p, i) => (
          <div
            key={p}
            className={`h-1.5 flex-1 rounded-full ${
              i <= paso ? 'bg-[var(--color-marca)]' : 'bg-gray-200'}`}
          />
        ))}
      </div>

      <div className="p-4 space-y-4 text-sm">
        {paso === 0 && <PasoMateriales />}
        {paso === 1 && (
          <PasoConexion cred={cred} error={errorCred} esAdmin={tieneNivel('ADMIN')} />
        )}
        {paso === 2 && <PasoEsperando nuevos={nuevos} />}
        {paso === 3 && <PasoUbicar nuevos={nuevos} onConfigurar={onConfigurar} />}
      </div>
    </Modal>
  );
}

function PasoMateriales() {
  return (
    <>
      <p className="text-gray-700 leading-relaxed">
        Vas a montar una tira de luces en un pasillo. Cuando alguien busque un
        producto en TEGO, se encenderá la lucecita justo enfrente de su charola.
      </p>
      <div>
        <div className="font-semibold text-gray-900 mb-1.5">Lo que necesitas</div>
        <ul className="space-y-1.5 text-gray-700">
          {[
            'Una tira de luces WS2812B (de las que llevan un cable de datos además de la corriente).',
            'Una tarjeta ESP32 con WiFi.',
            'Un cargador de 5 voltios con suficiente corriente para la tira.',
            'Una etiqueta y un plumón, para escribirle el nombre encima.',
            'El WiFi del almacén llegando hasta ese pasillo.',
          ].map((x) => (
            <li key={x} className="flex gap-2">
              <Check className="h-4 w-4 text-green-600 mt-0.5 shrink-0" aria-hidden="true" />
              <span>{x}</span>
            </li>
          ))}
        </ul>
      </div>
      <NotaExplicativa titulo="No hace falta que funcione hoy">
        TEGO funciona exactamente igual sin luces. Si algo no sale, cierra este
        asistente y sigue trabajando: buscar, contar y vender no dependen de esto.
      </NotaExplicativa>
    </>
  );
}

function PasoConexion({ cred, error, esAdmin }) {
  const [nombre, setNombre] = useState('');
  const [propia, setPropia] = useState(null);
  const [generando, setGenerando] = useState(false);
  const [fallo, setFallo] = useState(null);
  const [verEmpresa, setVerEmpresa] = useState(false);
  const { exito } = useAvisos();

  if (!esAdmin) {
    return (
      <NotaExplicativa titulo="Estos datos los tiene quien administra">
        La contraseña de conexión de los aparatos solo la puede ver un
        administrador. Pídesela a quien administra TEGO y sigue con el paso
        siguiente cuando la tengas.
      </NotaExplicativa>
    );
  }

  // Se limpia MIENTRAS SE ESCRIBE, no al guardar. Este nombre viaja dentro
  // del canal por el que habla el aparato: un espacio o un acento ahí rompe
  // cosas de formas difíciles de diagnosticar.
  const limpio = nombre.trim().toLowerCase().replace(/\s+/g, '-');
  const valido = /^[a-z0-9][a-z0-9._-]*$/.test(limpio) && limpio.length >= 2;

  async function generar() {
    setGenerando(true);
    setFallo(null);
    try {
      const c = await api.post('/dispositivos/credenciales', { identificador: limpio });
      setPropia(c);
      exito(`Lista la contraseña de «${c.identificador}».`);
    } catch (e) {
      setFallo(e instanceof ErrorApi ? e.message
        : 'No se pudo generar la contraseña. Vuelve a intentarlo.');
    } finally {
      setGenerando(false);
    }
  }

  if (propia) {
    return (
      <>
        <p className="text-gray-700 leading-relaxed">
          Copia estos cuatro datos en el programa de la tira (viene en el
          archivo <code className="mx-1 px-1 bg-gray-100 rounded text-xs">
          firmware/tira-led</code>),
          <strong> escribe «{propia.identificador}» en una etiqueta y pégala
          en la tira</strong>, y enchúfala.
        </p>
        <Credenciales cred={{ ...propia }} />
        <NotaExplicativa titulo="Esta contraseña abre solo esta tira">
          Si se filtra, se retira <strong>esta</strong> y se vuelve a programar
          <strong> esta</strong>: las demás siguen funcionando. Cada vez que
          alguien la consulta queda escrito en la auditoría.
        </NotaExplicativa>
      </>
    );
  }

  return (
    <>
      <p className="text-gray-700 leading-relaxed">
        Primero ponle nombre a la tira. TEGO le prepara una contraseña
        <strong> solo para ella</strong>, y esa es la que vas a teclear al
        programarla.
      </p>

      <label className="block">
        <span className="block text-sm font-semibold text-gray-700 mb-1">
          ¿Cómo se va a llamar esta tira?
        </span>
        <input
          className="entrada"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder="tira-pasillo-3"
          autoFocus
        />
        <span className="block text-xs text-gray-500 mt-1">
          {nombre.trim() === ''
            ? 'Algo corto que puedas escribir en una etiqueta: dónde está la tira.'
            : valido
              ? <>Se va a llamar <strong>{limpio}</strong>. Escríbelo en la
                  etiqueta tal cual.</>
              : 'Solo letras sin acentos, números y guiones. Por ejemplo: tira-pasillo-3'}
        </span>
      </label>

      {fallo && (
        <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {fallo}
        </div>
      )}

      <button
        type="button"
        onClick={generar}
        disabled={!valido || generando}
        title={valido ? undefined : 'Escribe un nombre para poder continuar'}
        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                   bg-[var(--color-marca)] text-white hover:opacity-90 disabled:opacity-50"
      >
        {generando && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        Preparar su contraseña
      </button>

      {/* LA DE EMPRESA SIGUE AHÍ, para las tiras que ya estén montadas con
          ella. No se quita: hay aparatos flasheados que no se van a
          reprogramar solos. Pero no es lo primero que se ofrece. */}
      <div className="pt-2 border-t border-gray-200">
        <button
          type="button"
          onClick={() => setVerEmpresa(!verEmpresa)}
          className="text-xs font-semibold text-gray-500 hover:text-gray-800"
        >
          {verEmpresa ? 'Ocultar' : '¿Tienes tiras montadas de antes? Ver la contraseña general'}
        </button>
        {verEmpresa && (
          <div className="mt-3 space-y-3">
            {error ? (
              <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                {error}
              </div>
            ) : !cred ? (
              <div className="flex items-center gap-2 text-gray-500 py-4 justify-center text-sm">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Buscando…
              </div>
            ) : (
              <>
                <Credenciales cred={cred} />
                <NotaExplicativa titulo="Ésta abre TODAS tus tiras">
                  Es la de antes y sigue funcionando. Pero con ella, una tira
                  colgada en un pasillo puede escuchar y mandar órdenes a todas
                  las demás — y si se filtra hay que reprogramarlas todas.
                  {' '}<strong>Para tiras nuevas, usa la de arriba.</strong>
                </NotaExplicativa>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// Los cuatro datos que se teclean al programar, con su botón de copiar.
function Credenciales({ cred }) {
  const { exito } = useAvisos();
  const campos = [
    { e: 'Servidor', v: cred.host, a: 'La dirección del servidor de TEGO en tu red.' },
    { e: 'Puerto', v: String(cred.puerto), a: 'Siempre el mismo.' },
    { e: 'Usuario', v: cred.usuario, a: 'Cópialo tal cual, con guiones incluidos.' },
    { e: 'Contraseña', v: cred.password, a: 'Guárdala: la vas a teclear al programar la tira.' },
  ];
  return (
    <div className="rounded-xl border border-gray-200 divide-y divide-gray-200">
      {campos.map((c) => (
        <div key={c.e} className="p-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs font-semibold text-gray-500">{c.e}</div>
            <div className="font-mono text-sm text-gray-900 break-all">{c.v || '—'}</div>
            <div className="text-xs text-gray-500 mt-0.5">{c.a}</div>
          </div>
          <button
            type="button"
            onClick={() => {
              navigator.clipboard?.writeText(c.v || '');
              exito(`Se copió ${c.e.toLowerCase()}.`);
            }}
            className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 shrink-0
                       rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
          >
            <Copy className="h-3.5 w-3.5" aria-hidden="true" />
            Copiar
          </button>
        </div>
      ))}
    </div>
  );
}


function PasoEsperando({ nuevos }) {
  if (nuevos.length === 0) {
    return (
      <div className="text-center py-6 space-y-3">
        <Radio className="h-8 w-8 text-[var(--color-marca)] mx-auto animate-pulse" aria-hidden="true" />
        <div className="font-semibold text-gray-800">Esperando a que la tira se anuncie…</div>
        <p className="text-gray-600 leading-relaxed max-w-sm mx-auto">
          En cuanto la enchufes y agarre WiFi, aparecerá aquí sola. No tienes que
          escribir nada.
        </p>
        <div className="text-left max-w-sm mx-auto bg-gray-50 rounded-xl p-3 space-y-1.5 text-gray-700">
          <div className="font-semibold text-gray-800">Si pasa un minuto y no aparece:</div>
          <ul className="space-y-1 list-disc pl-4">
            <li>Revisa que esté enchufada y que encienda alguna luz.</li>
            <li>Revisa que el WiFi llegue a ese pasillo con el teléfono.</li>
            <li>Revisa que copiaste bien la contraseña del paso anterior.</li>
          </ul>
        </div>
      </div>
    );
  }
  return (
    <>
      <div className="flex items-center gap-2 text-green-800 font-semibold">
        <Check className="h-5 w-5" aria-hidden="true" />
        {nuevos.length === 1 ? '¡Ya apareció!' : `¡Aparecieron ${nuevos.length}!`}
      </div>
      <ul className="space-y-2">
        {nuevos.map((d) => (
          <li key={d.id} className="rounded-xl border border-green-200 bg-green-50 p-3">
            <div className="font-semibold text-gray-900">{d.identificador}</div>
            <div className="text-xs text-gray-600">
              Se anunció <TiempoRelativo valor={d.ultimo_latido} />.
            </div>
          </li>
        ))}
      </ul>
      <p className="text-gray-600">
        Ya está hablando con TEGO. Falta decirle en qué pasillo la montaste.
      </p>
    </>
  );
}

function PasoUbicar({ nuevos, onConfigurar }) {
  if (nuevos.length === 0) {
    return (
      <p className="text-gray-700">
        Cuando la tira aparezca, ábrela desde la lista y usa
        <strong> Configurar</strong> para decirle en qué pasillo está y qué
        lucecita corresponde a cada charola.
      </p>
    );
  }
  return (
    <>
      <p className="text-gray-700 leading-relaxed">
        Último paso: dile dónde la montaste y qué lucecita enciende cada charola.
        Sin esto la tira está conectada pero no puede alumbrar nada.
      </p>
      <div className="space-y-2">
        {nuevos.map((d) => (
          <button
            key={d.id}
            type="button"
            onClick={() => onConfigurar(d)}
            className="w-full text-left rounded-xl border border-gray-200 p-3 hover:bg-gray-50
                       flex items-center justify-between gap-2"
          >
            <div>
              <div className="font-semibold text-gray-900">{d.identificador}</div>
              <div className="text-xs text-gray-500">Decirle dónde está</div>
            </div>
            <ChevronRight className="h-4 w-4 text-gray-400" aria-hidden="true" />
          </button>
        ))}
      </div>
    </>
  );
}

// -------------------------------------------------------- configuración
function ModalConfigurar({ d, onCerrar, onGuardado }) {
  const [nombre, setNombre] = useState(d.nombre || '');
  const [ubicacion, setUbicacion] = useState(d.ubicacion || '');
  const [mapa, setMapa] = useState(() => ({ ...(d.configuracion?.mapa || {}) }));
  // R-NFC2 (decisión del dueño, 17 sep 2026): qué HACE el aparato se pone
  // aquí, no con un UPDATE. Punto de fichaje = donde se pasa la tarjeta.
  const [funcion, setFuncion] = useState(d.funcion || 'BALDA');
  const [modoPresencia, setModoPresencia] = useState(d.modo_presencia || 'ALTERNAR');
  const [rebote, setRebote] = useState(d.rebote_segundos ?? 60);
  const { tieneNivel } = useAuth();
  const [ubicaciones, setUbicaciones] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const { exito, error: avisoError } = useAvisos();

  useEffect(() => {
    api.get('/ubicaciones').then(setUbicaciones).catch(() => setUbicaciones([]));
  }, []);

  // Los estantes que cuelgan del pasillo elegido. Es lo que convierte
  // «edita este JSON» en «dile qué lucecita es la charola 6».
  const estantes = useMemo(() => {
    if (!ubicacion) return [];
    const padre = ubicaciones.find((u) => u.codigo === ubicacion);
    if (!padre) return [];
    return ubicaciones.filter(
      (u) => u.activo && u.ruta && u.ruta.startsWith(`${padre.ruta}/`),
    );
  }, [ubicacion, ubicaciones]);

  const nombreValido = nombre.trim().length >= 2;

  async function guardar() {
    setGuardando(true);
    setError(null);
    try {
      // Solo se manda lo que tiene un número puesto: una charola sin LED
      // asignado no se guarda como «LED nulo», se queda fuera del mapa.
      const limpio = {};
      for (const [k, v] of Object.entries(mapa)) {
        if (v !== '' && v !== null && Number.isInteger(Number(v))) limpio[k] = Number(v);
      }
      await api.patch(`/dispositivos/${d.id}`, {
        nombre: nombre.trim(),
        ubicacion,
        configuracion: { ...(d.configuracion || {}), mapa: limpio },
      });
      if (tieneNivel('ADMIN') && (funcion !== (d.funcion || 'BALDA') || modoPresencia !== (d.modo_presencia || 'ALTERNAR') || Number(rebote) !== (d.rebote_segundos ?? 60))) {
        await api.post(`/dispositivos/${d.id}/funcion`, { funcion, modo_presencia: modoPresencia, rebote_segundos: Number(rebote) });
      }
      exito(`Se guardó «${nombre.trim()}».`);
      onGuardado();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message
        : 'No se pudo guardar. Vuelve a intentarlo.');
    } finally {
      setGuardando(false);
    }
  }

  async function probar(codigo) {
    try {
      const r = await api.post('/dispositivos/iluminar',
        { ubicacion: codigo, color: 'verde', segundos: 10 });
      if (r.entregado) exito(`Mira el pasillo: debería encenderse ${codigo}.`);
      else avisoError(r.detalle || 'No se pudo mandar la señal a la tira.');
    } catch (e) {
      avisoError(e instanceof ErrorApi ? e.message : 'No se pudo encender la luz.');
    }
  }

  return (
    <Modal
      titulo={`Configurar «${d.identificador}»`}
      subtitulo="Dónde está montada y qué lucecita enciende cada estante."
      icono={MapPin}
      ancho="max-w-lg"
      onCerrar={onCerrar}
      bloqueado={guardando}
      pie={(
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCerrar}
            disabled={guardando}
            className="px-3 py-2 rounded-lg text-sm font-semibold border border-gray-300
                       bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={guardar}
            disabled={guardando || !nombreValido}
            title={nombreValido ? undefined : 'Ponle un nombre de al menos dos letras'}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                       bg-[var(--color-marca)] text-white hover:opacity-90 disabled:opacity-50"
          >
            {guardando && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Guardar
          </button>
        </div>
      )}
    >
      <div className="p-4 space-y-4 text-sm">
        {error && (
          <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-red-800">
            {error}
          </div>
        )}

        <label className="block">
          <span className="block text-sm font-semibold text-gray-700 mb-1">
            Cómo se llama
          </span>
          <input
            className="entrada"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Tira del pasillo 3"
          />
          <span className="block text-xs text-gray-500 mt-1">
            {nombreValido
              ? 'El nombre con el que la verás en esta pantalla. La etiqueta pegada en el aparato no cambia.'
              : 'Ponle al menos dos letras para poder guardar.'}
          </span>
        </label>

        {tieneNivel('ADMIN') && (
          <fieldset className="block rounded-lg border border-gray-200 p-3">
            <legend className="px-1 text-sm font-semibold text-gray-700">Qué hace este aparato</legend>
            <select className="entrada" value={funcion} onChange={(e) => setFuncion(e.target.value)} aria-label="Función del aparato">
              <option value="BALDA">Tira de luz o balda (ilumina y pesa)</option>
              <option value="TERMINAL">Terminal de conteo</option>
              <option value="PRESENCIA">Punto de fichaje (aquí se pasa la tarjeta)</option>
              <option value="ESTACION">Estación de producción</option>
            </select>
            <span className="block text-xs text-gray-600 mt-1">
              Un punto de fichaje registra entradas y salidas del personal con su tarjeta o su PIN; una terminal de conteo abre sesión con gafete o tarjeta y cuenta.
            </span>
            {funcion === 'PRESENCIA' && (
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                <label className="block text-sm">
                  Cómo ficha
                  <select className="entrada" value={modoPresencia} onChange={(e) => setModoPresencia(e.target.value)}>
                    <option value="ALTERNAR">Alternar: una pasada entra, la siguiente sale</option>
                    <option value="BOTONES">Botones: la persona elige entrada o salida</option>
                  </select>
                </label>
                <label className="block text-sm">
                  Segundos entre pasadas de la misma tarjeta
                  <input type="number" min="0" max="3600" className="entrada" value={rebote} onChange={(e) => setRebote(e.target.value)} />
                  <span className="block text-xs text-gray-600 mt-1">Evita que un roce doble cuente como salida.</span>
                </label>
              </div>
            )}
          </fieldset>
        )}

        <label className="block">
          <span className="block text-sm font-semibold text-gray-700 mb-1">
            En qué pasillo la montaste
          </span>
          <select
            className="entrada"
            value={ubicacion}
            onChange={(e) => { setUbicacion(e.target.value); setMapa({}); }}
          >
            <option value="">— Sin decidir —</option>
            {ubicaciones.filter((u) => u.activo).map((u) => (
              <option key={u.id} value={u.codigo}>
                {u.ruta} · {u.nombre}
              </option>
            ))}
          </select>
          <span className="block text-xs text-gray-500 mt-1">
            Elige el pasillo entero, no un estante: una tira alumbra todo el
            pasillo.
          </span>
        </label>

        {ubicacion && (
          <div className="space-y-2">
            <div>
              <div className="font-semibold text-gray-800">
                Qué lucecita corresponde a cada estante
              </div>
              <p className="text-xs text-gray-500 leading-relaxed mt-0.5">
                Cuenta las lucecitas de la tira desde donde está el cable
                (la primera es la 0) y escribe el número que queda enfrente de
                cada estante. Con <strong>Probar</strong> se enciende y
                lo confirmas sin bajarte de la escalera.
              </p>
            </div>
            {estantes.length === 0 ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-900">
                De ese pasillo no cuelga ningún estante: no hay
                nada que alumbrar. Créalos en <strong>Almacén</strong> y vuelve
                aquí.
              </div>
            ) : (
              <ul className="divide-y divide-gray-200 border border-gray-200 rounded-xl">
                {estantes.map((u) => (
                  <li key={u.id} className="p-2.5 flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-gray-900 truncate">{u.codigo}</div>
                      <div className="text-xs text-gray-500 truncate">{u.nombre}</div>
                    </div>
                    <input
                      type="number"
                      min="0"
                      inputMode="numeric"
                      className="entrada w-24"
                      aria-label={`Número de lucecita para ${u.codigo}`}
                      value={mapa[u.codigo] ?? ''}
                      onChange={(e) => setMapa({ ...mapa, [u.codigo]: e.target.value })}
                      placeholder="—"
                    />
                    <button
                      type="button"
                      onClick={() => probar(u.codigo)}
                      disabled={!d.en_linea || mapa[u.codigo] === undefined || mapa[u.codigo] === ''}
                      title={d.en_linea
                        ? 'Guarda primero para probar un número nuevo'
                        : 'No se puede: esta tira no está respondiendo'}
                      className="text-xs font-semibold px-2 py-1.5 rounded-lg shrink-0
                                 border border-gray-300 bg-white text-gray-700
                                 hover:bg-gray-50 disabled:opacity-40"
                    >
                      Probar
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-xs text-gray-500">
              Los colores que puede encender son:{' '}
              {COLORES.map((c) => c.etiqueta.toLowerCase()).join(', ')}.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}
